<template>
  <div class="feed-post-chart">
    <StockChartTraderboard
      v-if="isCandlestick"
      :key="'tb-' + postId"
      :widget="widgetData"
      :showGrid="true"
    />
    <BarChart
      v-else-if="isBar"
      :key="'bar-' + postId"
      :showGrid="true"
      :showTitle="false"
      :colorsArr="chartColors"
      :widget="widgetData"
      :isCreateView="false"
    />
    <LineChart
      v-else
      :key="'line-' + postId"
      :showDuration="false"
      :showDaysFormat="true"
      :showGrid="true"
      :showHeader="false"
      :isDataIndirect="false"
      :colorsArr="chartColors"
      :widget="widgetData"
      :isCreateView="false"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import StockChartTraderboard from "./StockChartTraderboard.vue";
import BarChart from "./barChart.vue";
import LineChart from "./LineChart.vue";
import { ConfigurationWidgetModel } from "../../models/configurewidget.model";

export default defineComponent({
  name: "FeedCandleChart",
  components: {
    StockChartTraderboard,
    BarChart,
    LineChart,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const postId = computed(() => props.post?._id || "chart");

    const chartType = computed(() =>
      String(props.post?.chart?.type || "line").toLowerCase()
    );

    const isCandlestick = computed(
      () =>
        chartType.value === "candlestick" ||
        chartType.value === "candlestickchart" ||
        chartType.value === "candle"
    );

    const isBar = computed(
      () => chartType.value === "bar" || chartType.value === "barchart"
    );

    const chartColors = computed(() => {
      const colors = props.post?.chart?.colors;
      if (Array.isArray(colors) && colors.length) return colors;
      return ["#8B77CF"];
    });

    const resolveStock = () => {
      const stock = props.post?.chart?.stocks;
      if (stock?.symbol || stock?.stockId) return { ...stock };

      const tag = props.post?.stockTags?.[0];
      if (!tag) return null;

      return {
        symbol: String(tag.symbol || "").replace(/^\$/, ""),
        stockId: tag.stockId,
        type: tag.market || "stocks",
        exchange: "NASDAQ",
      };
    };

    const resolveChartData = () => {
      const chart = props.post?.chart;
      if (!chart) return [];

      if (isCandlestick.value) {
        if (chart.data?.length) return chart.data;
        if (chart.candleData) {
          try {
            const parsed =
              typeof chart.candleData === "string"
                ? JSON.parse(chart.candleData)
                : chart.candleData;
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            return [];
          }
        }
        return [];
      }

      return Array.isArray(chart.data) ? chart.data : [];
    };

    const widgetData = computed(() => {
      const stock = resolveStock();
      return new ConfigurationWidgetModel({
        stock: stock || undefined,
        chartData: resolveChartData(),
        duration: props.post?.chart?.duration || "1mo",
        interval: props.post?.chart?.interval || "1d",
        dataType: props.post?.chart?.dataType || [],
      });
    });

    return {
      postId,
      isCandlestick,
      isBar,
      chartColors,
      widgetData,
    };
  },
});
</script>

<style scoped>
.feed-post-chart {
  width: 100%;
  height: 340px;
  min-height: 340px;
  padding: 8px 12px 12px;
  background: var(--base-dark);
  border-radius: 10px;
  margin-top: 8px;
  box-sizing: border-box;
}

.feed-post-chart :deep(.new_chart_container) {
  height: 100% !important;
  min-height: 300px;
  width: 100%;
}

.feed-post-chart :deep(.vue-apexcharts),
.feed-post-chart :deep(.apexcharts-canvas) {
  min-height: 280px !important;
}
</style>
